import { DIALOG_DATA } from '@angular/cdk/dialog';
import { afterRenderEffect, ChangeDetectionStrategy, Component, computed, inject, Signal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormValid } from '@components/formulaire-rdv/models';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { PopinContainerComponent } from '@shared/components/popin-container';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/models';
import { HandleHtmlLinksDirective } from '@shared/directives';
import { SafeHtmlPipe } from '@shared/pipe';
import { ContactEstimatorService } from '@shared/services/contact-estimator';
import { ContactEstimatorQuery } from '@shared/services/contact-estimator/models';
import { DataLayerService } from '@shared/services/data-layer';
import { GtmEvent } from '@shared/services/data-layer/models';
import { LanguageService } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { isEmailValid, normalizeAndHash } from '@shared/utils';
import { CommonStore } from '@stores';
import { BudgetEstimatorEstimate } from '@stores/models';
import { catchError, EMPTY, of, switchMap, take, tap } from 'rxjs';

@Component({
  selector: 'app-popin-estimation-email',
  imports: [
    PopinContainerComponent,
    TitleComponent,
    TranslatePipe,
    SafeHtmlPipe,
    ButtonComponent,
    HandleHtmlLinksDirective,
  ],
  providers: [ContactEstimatorService],
  templateUrl: './popin-estimation-email.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopinEstimationEmailComponent {
  data = inject(DIALOG_DATA) as {
    estimate: BudgetEstimatorEstimate;
    gtmEventForEmailSent: Partial<GtmEvent>;
  };
  protected readonly email = signal<string>('');
  protected readonly emailNormalized = toSignal(
    toObservable(this.email).pipe(
      switchMap((email) =>
        normalizeAndHash(email).pipe(
          catchError((err: unknown) => {
            this.logs.error('PopinEstimationEmailComponent', 'Failed to hash email', err);
            return of('');
          })
        )
      )
    )
  );
  protected readonly isFormSubmitted = signal(false);
  protected readonly isFormValid: Signal<FormValid> = computed(() => {
    let result: FormValid = {
      status: 'OK',
    };
    const errors: FormValid['errors'] = {};

    if (this.email().length === 0) {
      errors.email = 'GLOBAL.FORMS.EMAIL_REQUIRED_ERROR';
    } else if (!isEmailValid(this.email())) {
      errors.email = 'GLOBAL.FORMS.EMAIL_INVALID_ERROR';
    }

    if (Object.keys(errors).length > 0) {
      result = {
        status: 'KO',
        errors: errors,
      };
    }

    return result;
  });
  protected readonly isSending = signal(false);
  protected readonly titleForm = signal<Title>(null!);
  protected readonly titleConfirmation = signal<Title>(null!);
  protected readonly privacyPolicy = signal<string>(null!);
  protected readonly isEstimateEmailed = signal(false);
  protected readonly estimateSentConfirmation = signal<string>(null!);
  private readonly contactEstimatorService = inject(ContactEstimatorService);
  private readonly privacyPolicyUrl = inject(CommonStore).select('privacyPolicyUrl');
  private readonly languageService = inject(LanguageService);
  private readonly dataLayerService = inject(DataLayerService);
  private readonly logs = inject(LogsService);

  constructor() {
    afterRenderEffect(() => {
      const textForm = this.languageService.instant('COMPONENT.POPIN-ESTIMATION-EMAIL.SEND_KITCHEN_ESTIMATE_BY_EMAIL');
      const textConfirmation = this.languageService.instant('COMPONENT.POPIN-ESTIMATION-EMAIL.ALL_GOOD');
      const privacyPolicy = this.languageService.instant('COMPONENT.POPIN-ESTIMATION-EMAIL.PRIVACY_POLICY_REMINDER', {
        privacyPolicyUrl: this.privacyPolicyUrl() ?? '',
      });
      this.titleForm.set({
        text: textForm,
        as: 'span',
      });
      this.titleConfirmation.set({
        text: textConfirmation,
        as: 'span',
      });
      this.privacyPolicy.set(privacyPolicy);
    });
  }

  onInputChange($event: Event) {
    const target = $event.target as HTMLInputElement;
    const { id, value, checked, type } = target;
    this.logs.trace(
      'PopinEstimationEmailComponent',
      `onInputChange - ${id} : ${type === 'checkbox' ? checked : value}`
    );
    if (id === 'email') {
      this.email.set(value);
    }
  }

  onSubmit(): void {
    if (this.isSending()) return;
    this.logs.debug('PopinEstimationEmailComponent', 'onSubmit', this.isFormValid());
    this.isFormSubmitted.set(true);
    if (this.isFormValid().status === 'OK') {
      this.isSending.set(true);
      const gtm = {
        ...this.data.gtmEventForEmailSent,
        user_email: this.emailNormalized(),
      };
      const toSaved: ContactEstimatorQuery = {
        email: this.email(),
        configuration: [this.data.estimate],
      };
      this.contactEstimatorService
        .create(toSaved, true)
        .pipe(
          take(1),
          tap(() => {
            if (gtm?.event && gtm?.event.length > 0) {
              this.dataLayerService.push(gtm);
            }
            const estimateSentConfirmation = this.languageService.instant(
              'COMPONENT.POPIN-ESTIMATION-EMAIL.ESTIMATE_SENT_CONFIRMATION',
              { email: this.email() }
            );
            this.estimateSentConfirmation.set(estimateSentConfirmation);
            this.isEstimateEmailed.set(true);
          }),
          catchError((e) => {
            this.logs.error('PopinEstimationEmailComponent', 'Failed to send the estimate by email', e);
            this.isSending.set(false);
            return EMPTY;
          })
        )
        .subscribe();
    }
  }
}
